import classNames from "classnames";
import { CalendarMode, Row } from "PFApp/booking/types";
import { RowType } from "PFApp/booking/types/row";
import React, { JSX, useEffect } from "react";

import { useBookingsOverviewContext } from "../context/bookings_overview_context";
import css from "../side_panel_rows.module.scss";
import { useUtilization } from "./hooks";
import { InfoText } from "./info_text";
import { RowBadge } from "./row_badge";
import { RowExpander } from "./row_expander";
import { RowHoursBooked } from "./row_hours_booked";
import { RowName } from "./row_name";
import { SidePanelRowActions } from "./side_panel_row_actions";
import { useAllBookingsMode } from "./use_all_bookings_mode";
import Utilization from "./utilization";
import WarningIconsPanel from "./warning_icons_panel";

type RowContentProps = {
  row: Row;
  expandable: boolean;
  expanded: boolean;
  loading: boolean;
  onAllBookingsModeEnable: VoidFunction;
  onExpandedToggle: VoidFunction;
  onCollapse: VoidFunction;
  scrollToDate: (date: string) => void;
  childrenCount: number;
};

export const RowContent = ({
  row,
  expandable,
  expanded,
  loading,
  onAllBookingsModeEnable,
  onExpandedToggle,
  onCollapse,
  scrollToDate,
  childrenCount
}: RowContentProps): JSX.Element => {
  const { calendarMode } = useBookingsOverviewContext();
  const { shouldDisplayUtilization, utilizationValue } = useUtilization({ row });

  const { isAllBookingsModeEnabled, handleAllBookingsModeChange } = useAllBookingsMode({
    row,
    onAllBookingsModeEnable,
    childrenCount
  });

  useEffect(() => {
    if ((expandable && !expanded) || (!loading && childrenCount === 0)) {
      calendarMode === CalendarMode.Projects &&
        handleAllBookingsModeChange({ isAllBookingsModeEnabled: false, propagate: false });
      onCollapse();
    }
  }, [expanded, childrenCount, loading]);

  const { type } = row;
  const shouldDisplayActivityActions =
    calendarMode === CalendarMode.Projects && [RowType.Role, RowType.Engagement].includes(type);
  return (
    <>
      {shouldDisplayActivityActions && (
        <SidePanelRowActions
          className={classNames({
            [css.loading]: loading
          })}
          showAllBookingsAction={{
            isAllBookingsModeEnabled: isAllBookingsModeEnabled && expanded,
            handleAllBookingsModeChange: (isAllBookingsModeEnabled) =>
              handleAllBookingsModeChange({ isAllBookingsModeEnabled })
          }}
        />
      )}
      <div className={css.nameAndButtons}>
        <RowExpander
          row={row}
          onClick={onExpandedToggle}
          disabled={expanded && loading}
          expanded={expanded && !!childrenCount}
        />
        <div
          className={classNames(css.itemContent, {
            [css.noExpandArrow]: !expandable
          })}
        >
          <div className={css.itemContentTop}>
            <div className={css.itemContent}>
              <RowName row={row} />
              <RowBadge row={row} />
            </div>
            <InfoText row={row} />
            <WarningIconsPanel row={row} isRowExpanded={expanded} scrollToDate={scrollToDate} />
          </div>
          {shouldDisplayUtilization && <Utilization value={utilizationValue!} />}
        </div>
      </div>
      <RowHoursBooked row={row} />
    </>
  );
};
